import { TireSeason, type WheelStorageCarChangeRecordOutput } from '@navio/nsdk';
import { invalidateOwnershipTransferQueries, noddiAsync, URLKeys } from 'noddi-async';
import { ApiErrorMessageInternal, NoddiButton, NoddiDialog, NoddiInfoBanner, useAppForm } from 'noddi-ui-web';
import { TIRE_SEASON_OPTIONS } from 'noddi-util';
import { z } from 'zod';
import { TanstackUserGroupAddressAsyncSelect } from '../../form/tanstack/features/Account/TanstackUserGroupAddressAsyncSelect';
import { TanstackUserGroupCarAsyncSelect } from '../../form/tanstack/features/Account/TanstackUserGroupCarAsyncSelect';

const FORM_ID = 'car-change-form';

const option = z.object({ value: z.number(), label: z.string() });

const validationSchema = z.object({
  toCar: option.optional().refine((value) => value != null, 'Choose the new car'),
  address: option.optional().refine((value) => value != null, 'Choose the customer’s address'),
  tireSeasons: z.array(z.enum(TireSeason)).min(1, 'Choose at least one season')
});

type FormValues = z.input<typeof validationSchema>;

type Props = {
  fromCarId: number;
  userGroupId: number;
  serviceDepartmentId: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess?: (result: WheelStorageCarChangeRecordOutput) => void;
};

/**
 * The same customer goes on with tire hotel on a new car: the old car's tire hotel ends, the new car gets one, and
 * the wheel sets of the chosen seasons move to the new car with their label, which then needs the new license plate.
 */
export const CarChangeDialog = ({
  fromCarId,
  userGroupId,
  serviceDepartmentId,
  open,
  onOpenChange,
  onSuccess
}: Props) => {
  const {
    mutateAsync: changeCar,
    error,
    reset: resetChangeCar
  } = noddiAsync.usePost({
    type: URLKeys.postWheelStorageCarChange,
    queryConfig: { meta: { suppressErrorToast: true }, onSettled: invalidateOwnershipTransferQueries }
  });

  const defaultValues: FormValues = { toCar: undefined, address: undefined, tireSeasons: [] };

  const form = useAppForm({
    defaultValues,
    validators: { onSubmit: validationSchema },
    onSubmit: async ({ value }) => {
      const response = await changeCar({
        userGroupId,
        serviceDepartmentId,
        fromCarId,
        toCarId: value.toCar!.value,
        addressId: value.address!.value,
        tireSeasons: value.tireSeasons
      });
      onSuccess?.(response.data);
      close();
    }
  });

  // The dialog stays mounted between openings; the last attempt's choices and refusal must not greet the next one.
  const close = () => {
    resetChangeCar();
    form.reset();
    onOpenChange(false);
  };

  return (
    <form.AppForm>
      <NoddiDialog
        open={open}
        onClose={close}
        title='Change car'
        description='The tire hotel continues on the new car, and the wheels stay in storage.'
        footer={
          <div className='flex justify-end gap-3 border-t pt-4'>
            <NoddiButton variant='outline' onClick={close}>
              Cancel
            </NoddiButton>
            <form.SubscribeButton formId={FORM_ID} label='Change car' />
          </div>
        }
      >
        <form
          id={FORM_ID}
          className='flex flex-col gap-4'
          onSubmit={(event) => {
            event.preventDefault();
            event.stopPropagation();
            void form.handleSubmit();
          }}
        >
          {/* role='alert': with no toast, this is what a screen reader announces. */}
          <div role='alert'>{error && <ApiErrorMessageInternal error={error} />}</div>
          <form.AppField name='toCar'>
            {() => (
              <TanstackUserGroupCarAsyncSelect
                label='New car'
                userGroupId={userGroupId}
                valueFrom='car'
                excludeCarIds={[fromCarId]}
              />
            )}
          </form.AppField>
          <form.AppField name='tireSeasons'>
            {(field) => (
              <field.MultiSelect
                label='Seasons in storage'
                options={TIRE_SEASON_OPTIONS}
                placeholder='Choose the seasons the new car stores'
              />
            )}
          </form.AppField>
          <form.AppField name='address'>
            {() => <TanstackUserGroupAddressAsyncSelect label='Customer address' userGroupId={userGroupId} />}
          </form.AppField>
          <NoddiInfoBanner
            variant='info'
            icon='Tag'
            texts={[
              {
                value:
                  'Wheel sets of these seasons move to the new car and stay in the same storage unit. Their label shows the old license plate, so they are marked “Needs a new label”: print a new label and put it on. Wheel sets of other seasons are delivered out.'
              }
            ]}
          />
        </form>
      </NoddiDialog>
    </form.AppForm>
  );
};
